import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  DndContext,
  useSensor,
  useSensors,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  pointerWithin,
  rectIntersection,
  useDraggable,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  Card,
  Input,
  Space,
  Typography,
  Badge,
  Tag,
  Empty,
  Button,
  Modal,
  List,
  Checkbox,
  Switch,
} from "antd";
import {
  SearchOutlined,
  HolderOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { DroppableList } from "./DREConfig/DroppableList";
import { SortableCard } from "./DREConfig/SortableCard";
import { useTheme } from "../../../contexts/ThemeContext";
import {
  filterOmieFinancialCategories,
  filterOmieInvalidKpiOrGroupConfigRows,
} from "../../../utils/omieFinancialCategoryFilter";
import {
  flattenGroups,
  groupLeavesByAncestors,
  isRootParent,
  supportsMultiLevelCategories,
} from "../../../utils/categoryAncestors";

const { Text } = Typography;
const ZERO_GUID = "00000000-0000-0000-0000-000000000000";

const DraggableGroupHeader = ({ groupKey, children }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `group:${groupKey}`,
    });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    zIndex: isDragging ? 999 : undefined,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? "grabbing" : "grab",
    touchAction: "none",
  };

  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
      {children}
    </div>
  );
};

const DragDropConfigurator = ({
  types, // Object: { key: { label, value, color? } }
  categories, // Array: [{ id, codCategoria, descricao, natureza... }]
  currentConfig, // Array: [{ codCategoria, type/financialGroup... }]
  onConfigChange, // Function: (newConfigArray) => void
  parameterType, // "indicadores" | "gruposFinanceiros" | "dfc"
  searchTerm = "",
  erp,
  showRevenueToggle = false,
  allowRevenue = false,
  onAllowRevenueChange,
}) => {
  const { theme } = useTheme();
  const [activeId, setActiveId] = useState(null);
  // Conta Azul aceita até 3 níveis: os grupos seguem toda a cadeia de ancestrais (com 2 níveis a
  // cadeia é só o pai direto, igual ao agrupamento de antes).
  const multiLevel = supportsMultiLevelCategories(erp);

  const applyOmieFinancialCategoryRules = useMemo(
    () =>
      Number(erp) === 0 &&
      (parameterType === "indicadores" ||
        parameterType === "gruposFinanceiros"),
    [erp, parameterType],
  );

  const effectiveCategories = useMemo(() => {
    let result = applyOmieFinancialCategoryRules
      ? filterOmieFinancialCategories(categories || [])
      : categories || [];
    if (
      !allowRevenue &&
      (parameterType === "gruposFinanceiros" ||
        parameterType === "indicadores")
    ) {
      result = result.filter((c) => c.natureza !== "R");
    }
    return result;
  }, [
    categories,
    applyOmieFinancialCategoryRules,
    parameterType,
    allowRevenue,
  ]);

  useEffect(() => {
    if (!applyOmieFinancialCategoryRules || typeof onConfigChange !== "function")
      return;
    const rows = currentConfig || [];
    const filtered = filterOmieInvalidKpiOrGroupConfigRows(
      rows,
      categories || [],
    );
    if (filtered.length !== rows.length) {
      onConfigChange(filtered);
    }
  }, [
    applyOmieFinancialCategoryRules,
    categories,
    currentConfig,
    onConfigChange,
  ]);

  // Bulk Modal State
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);
  const [bulkTargetType, setBulkTargetType] = useState(null);
  const [bulkSelectedCatCodes, setBulkSelectedCatCodes] = useState([]);
  const [modalSearch, setModalSearch] = useState("");

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 150, tolerance: 8 },
    }),
  );
  const normalizeCategoryCode = (value) =>
    value === undefined || value === null ? "" : String(value).trim();

  const findCategoryByCode = (code) => {
    const normalizedCode = normalizeCategoryCode(code);
    return (effectiveCategories || []).find(
      (c) => normalizeCategoryCode(c.codCategoria) === normalizedCode,
    );
  };

  const hierarchyIndex = useMemo(() => {
    const byCode = new Map();
    const parentByCode = new Map();
    const childrenByParent = new Map();
    const parentCodes = new Set();

    (effectiveCategories || []).forEach((cat) => {
      const code = normalizeCategoryCode(cat.codCategoria);
      if (!code) return;
      const parentCode = normalizeCategoryCode(cat.codCategoriaSup);
      byCode.set(code, cat);
      parentByCode.set(code, parentCode);

      if (!isRootParent(parentCode)) {
        parentCodes.add(parentCode);
        if (!childrenByParent.has(parentCode)) childrenByParent.set(parentCode, []);
        childrenByParent.get(parentCode).push(code);
      }
    });

    return {
      byCode,
      parentByCode,
      childrenByParent,
      parentCodes,
    };
  }, [effectiveCategories]);

  const isSelectableCategoryCode = useCallback(
    (rawCode) => {
      const code = normalizeCategoryCode(rawCode);
      if (!code) return false;
      const parentCode = hierarchyIndex.parentByCode.get(code);
      const isRoot = parentCode === ZERO_GUID;
      const isParent = hierarchyIndex.parentCodes.has(code);
      // Em DFC e Grupos Financeiros, categorias folha (sem filhas) devem ser
      // selecionáveis mesmo quando não possuem categoria superior (raízes-folha).
      if (parameterType === "dfc" || parameterType === "gruposFinanceiros") {
        return !isParent;
      }
      return !isRoot && !isParent;
    },
    [hierarchyIndex, parameterType],
  );

  // Normaliza os tipos para array
  const typeList = useMemo(() => {
    if (!types) return [];
    return Object.values(types).sort((a, b) => a.value - b.value);
  }, [types]);

  // Mapeia configurações atuais para fácil acesso
  // Map: TypeValue -> Array of CategoryIds (codCategoria)
  const configMap = useMemo(() => {
    const map = {};
    typeList.forEach((t) => {
      map[t.value] = [];
    });

    (currentConfig || []).forEach((item) => {
      // Identifica o valor do tipo baseado no parâmetro
      const typeVal = Number(
        item.type !== undefined
          ? item.type
          : item.Type !== undefined
            ? item.Type
            : item.financialGroup !== undefined
              ? item.financialGroup
              : item.FinancialGroup !== undefined
                ? item.FinancialGroup
                : 0,
      );

      const catCod = normalizeCategoryCode(item.codCategoria || item.CodCategoria);
      if (Object.prototype.hasOwnProperty.call(map, typeVal) && catCod) {
        map[typeVal].push(catCod);
      }
    });
    return map;
  }, [currentConfig, typeList]);

  // Categorias disponíveis (não vinculadas a nenhum tipo DESTE parâmetro)
  const linkedCategories = useMemo(() => {
    const linked = new Set();
    Object.values(configMap).forEach((cats) => {
      cats.forEach((c) => linked.add(normalizeCategoryCode(c)));
    });
    return linked;
  }, [configMap]);

  const unassignedCategories = useMemo(
    () =>
      effectiveCategories.filter(
        (c) => !linkedCategories.has(normalizeCategoryCode(c.codCategoria)),
      ),
    [effectiveCategories, linkedCategories],
  );

  // Versão filtrada para a lista lateral (usa o search global)
  const availableCategories = useMemo(() => {
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      return unassignedCategories.filter(
        (c) =>
          c.descricao.toLowerCase().includes(lowerSearch) ||
          String(c.codCategoria).includes(lowerSearch),
      );
    }
    return unassignedCategories;
  }, [unassignedCategories, searchTerm]);

  const availableSelectableCategories = useMemo(
    () =>
      availableCategories.filter((c) =>
        isSelectableCategoryCode(c.codCategoria),
      ),
    [availableCategories, isSelectableCategoryCode],
  );

  // Fonte para resolver pais/rótulos dos grupos. No multinível usa a lista completa, para um
  // ancestral filtrado (ex.: natureza) não quebrar a cadeia.
  const hierarchySource = useMemo(
    () => effectiveCategories || [],
    [effectiveCategories],
  );

  const availableCategoriesGrouped = useMemo(
    () =>
      groupLeavesByAncestors(availableSelectableCategories, hierarchySource, {
        multiLevel,
      }),
    [availableSelectableCategories, hierarchySource, multiLevel],
  );

  const findAvailableGroup = (groupKey) =>
    flattenGroups(availableCategoriesGrouped).find((g) => g.key === groupKey);

  // Handler para Drag End
  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const activeIdString = String(active.id);

    // ----------------------------------------------------------------
    // 1. Logica de Grupo (NOVO)
    // ----------------------------------------------------------------
    if (activeIdString.startsWith("group:")) {
      const groupKey = activeIdString.replace("group:", "");
      // Encontrar o grupo (em qualquer nível) na árvore memoizada
      const group = findAvailableGroup(groupKey);
      if (!group) return;

      // Determinar destino
      const overId = String(over.id);
      let targetTypeId = null;

      if (overId.startsWith("type-container:")) {
        targetTypeId = Number(overId.split(":")[1]);
      } else if (overId.startsWith("cat:")) {
        // Soltou sobre outra categoria -> descobrir o container dela
        const overCatCode = overId.replace("cat:", "");
        for (const [typeVal, cats] of Object.entries(configMap)) {
          if (cats.includes(overCatCode)) {
            targetTypeId = Number(typeVal);
            break;
          }
        }
      }

      // Se soltou em "available-container" ou fora de um tipo, targetTypeId é null.
      // Se targetTypeId for null, não fazemos nada (já estão disponíveis)
      if (targetTypeId === null) return;

      // Adicionar todas as categorias do grupo (e subgrupos) ao tipo destino
      handleAssignGroupToType(group.allCategories, targetTypeId);
      return;
    }

    // ----------------------------------------------------------------
    // 2. Logica de Categoria Individual (EXISTENTE)
    // ----------------------------------------------------------------
    const activeCatId = activeIdString.replace("cat:", ""); // ID da categoria arrastada
    const overId = String(over.id); // ID do destino (pode ser container ou outra categoria)

    // Encontrar a categoria objeto
    const normalizedActiveCatId = normalizeCategoryCode(activeCatId);
    const categoryObj = effectiveCategories.find((c) => {
      const categoryCode = normalizeCategoryCode(c.codCategoria);
      const categoryId = normalizeCategoryCode(c.id);
      return categoryCode === normalizedActiveCatId || categoryId === normalizedActiveCatId;
    });
    if (!categoryObj) return;
    const catCode = normalizeCategoryCode(categoryObj.codCategoria);
    if (!isSelectableCategoryCode(catCode)) return;

    // Determinar origem e destino
    let sourceTypeId = null; // null significa "Disponíveis"
    let targetTypeId = null;

    // Encontrar onde estava antes
    for (const [typeVal, cats] of Object.entries(configMap)) {
      if (cats.includes(catCode)) {
        sourceTypeId = Number(typeVal);
        break;
      }
    }

    // Determinar para onde foi
    if (overId === "available-container") {
      targetTypeId = null;
    } else if (overId.startsWith("type-container:")) {
      targetTypeId = Number(overId.split(":")[1]);
    } else if (overId.startsWith("cat:")) {
      // Soltou sobre outra categoria
      const overCatCode = normalizeCategoryCode(overId.replace("cat:", ""));
      // Descobrir em qual container essa categoria está
      for (const [typeVal, cats] of Object.entries(configMap)) {
        if (cats.includes(overCatCode)) {
          targetTypeId = Number(typeVal);
          break;
        }
      }
      // Se não achou nos vinculados, assume que soltou sobre uma categoria da lista de disponíveis
      if (targetTypeId === null) {
        // Verifica se está na lista de disponíveis (apenas para garantir)
        // Se soltou sobre uma disponível, o target é null (Disponíveis)
        targetTypeId = null;
      }
    }

    // Se origem e destino são iguais, nada a fazer (apenas reordenação visual se implementado, mas aqui a ordem não é persistida estritamente no config array da mesma forma que DRE)
    if (sourceTypeId === targetTypeId) return;

    // Atualizar configuração
    let newConfig = [...(currentConfig || [])];

    // 1. Remover da origem (se não for null)
    if (sourceTypeId !== null) {
      newConfig = newConfig.filter((item) => {
        const typeVal = Number(
          item.type !== undefined
            ? item.type
            : item.Type !== undefined
              ? item.Type
              : item.financialGroup !== undefined
                ? item.financialGroup
                : item.FinancialGroup !== undefined
                  ? item.FinancialGroup
                  : 0,
        );
        const itemCatCode = normalizeCategoryCode(item.codCategoria || item.CodCategoria);
        return !(typeVal === sourceTypeId && itemCatCode === catCode);
      });
    }

    // 2. Adicionar ao destino (se não for null)
    if (targetTypeId !== null) {
      const newItem = {
        codCategoria: catCode,
        // Preencher o campo correto baseado no parameterType
        ...(parameterType === "gruposFinanceiros"
          ? { financialGroup: targetTypeId }
          : { type: targetTypeId }),
        // Campos auxiliares podem ser necessários dependendo do backend, mas geralmente o ID da categoria e o Tipo bastam
        // Se precisar de mais dados do objeto categoria, pode adicionar aqui
        Id: categoryObj.id,
      };
      newConfig.push(newItem);
    }

    onConfigChange(newConfig);
  };

  const handleUnlink = (catCode) => {
    // Remove a categoria de qualquer vínculo
    let newConfig = [...(currentConfig || [])];
    const normalizedCatCode = normalizeCategoryCode(catCode);
    newConfig = newConfig.filter((item) => {
      const itemCatCode = normalizeCategoryCode(item.codCategoria || item.CodCategoria);
      return itemCatCode !== normalizedCatCode;
    });
    onConfigChange(newConfig);
  };

  const dicNatureza = {
    P: "Despesa",
    R: "Receita",
  };

  // --- Bulk Add Modal Logic ---
  const handleBulkOpen = (typeVal) => {
    setBulkTargetType(typeVal);
    setBulkSelectedCatCodes([]);
    setModalSearch("");
    setIsBulkModalOpen(true);
  };

  const addCategoriesToType = (catCodes, targetTypeId) => {
    if (targetTypeId === null || targetTypeId === undefined) return;

    const existingCodes = new Set(
      (currentConfig || []).map((item) =>
        normalizeCategoryCode(item.codCategoria || item.CodCategoria),
      ),
    );

    const newItems = catCodes
      .map((code) => normalizeCategoryCode(code))
      .filter(
        (code) =>
          code &&
          !existingCodes.has(code) &&
          isSelectableCategoryCode(code),
      )
      .map((code) => {
        const catObj = findCategoryByCode(code);
        return {
          codCategoria: code,
          ...(parameterType === "gruposFinanceiros"
            ? { financialGroup: targetTypeId }
            : { type: targetTypeId }),
          Id: catObj?.id,
        };
      });

    if (newItems.length === 0) return;
    onConfigChange([...(currentConfig || []), ...newItems]);
  };

  const handleAssignGroupToType = (groupCategories, targetTypeId) => {
    const groupCodes = (groupCategories || []).map((c) => c.codCategoria);
    addCategoriesToType(groupCodes, targetTypeId);
  };

  const handleBulkSave = () => {
    if (bulkTargetType === null) return;

    addCategoriesToType(bulkSelectedCatCodes, bulkTargetType);
    setIsBulkModalOpen(false);
    setBulkTargetType(null);
    setBulkSelectedCatCodes([]);
  };

  const filteredCatsForModal = useMemo(() => {
    if (!isBulkModalOpen) return [];
    const bulkBase = unassignedCategories.filter((c) =>
      isSelectableCategoryCode(c.codCategoria),
    );
    if (!modalSearch) return bulkBase;
    const lower = modalSearch.toLowerCase();
    return bulkBase.filter(
      (c) =>
        c.descricao.toLowerCase().includes(lower) ||
        String(c.codCategoria).includes(lower),
    );
  }, [isBulkModalOpen, modalSearch, unassignedCategories, isSelectableCategoryCode]);

  const filteredCatsForModalGrouped = useMemo(
    () =>
      groupLeavesByAncestors(filteredCatsForModal, hierarchySource, {
        multiLevel,
      }),
    [filteredCatsForModal, hierarchySource, multiLevel],
  );

  const handleBulkToggle = (code) => {
    setBulkSelectedCatCodes((prev) => {
      if (prev.includes(code)) return prev.filter((c) => c !== code);
      return [...prev, code];
    });
  };

  const handleBulkSelectAll = () => {
    const allCodes = filteredCatsForModal
      .map((c) => c.codCategoria)
      .filter((code) => isSelectableCategoryCode(code));
    setBulkSelectedCatCodes((prev) => {
      const set = new Set([...prev, ...allCodes]);
      return Array.from(set);
    });
  };

  const handleBulkDeselectAll = () => {
    const visibleCodes = filteredCatsForModal
      .map((c) => c.codCategoria)
      .filter((code) => isSelectableCategoryCode(code));
    setBulkSelectedCatCodes((prev) =>
      prev.filter((c) => !visibleCodes.includes(c)),
    );
  };

  const handleBulkToggleGroup = (groupCategories) => {
    const groupCodes = groupCategories
      .map((c) => c.codCategoria)
      .filter((code) => isSelectableCategoryCode(code));
    setBulkSelectedCatCodes((prev) => {
      const allSelected = groupCodes.every((code) => prev.includes(code));
      if (allSelected) {
        return prev.filter((code) => !groupCodes.includes(code));
      }
      const set = new Set([...prev, ...groupCodes]);
      return Array.from(set);
    });
  };

  // Renderização de um Card de Categoria
  const renderCategoryCard = (
    catCode,
    isOverlay = false,
    isAvailable = false,
  ) => {
    const normalizedCatCode = normalizeCategoryCode(catCode);
    const cat = findCategoryByCode(normalizedCatCode);
    if (!cat) return null;

    return (
      <SortableCard
        key={`cat:${normalizedCatCode}`}
        id={`cat:${normalizedCatCode}`}
        className="cat-chip"
        bodyStyle={{ padding: "8px 12px" }}
        style={{
          backgroundColor: theme.cardBackground,
          border: `1px solid ${theme.borderSecondary}`,
          borderRadius: 4,
          marginBottom: 8,
          cursor: isOverlay ? "grabbing" : "grab",
          boxShadow: isOverlay ? "0 5px 15px rgba(0,0,0,0.15)" : "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <HolderOutlined
            style={{ fontSize: 14, color: theme.textSecondary }}
          />
          <div
            style={{
              display: "flex",
              flex: 1,
              minWidth: 0,
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text
              strong
              style={{
                fontSize: 12,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "block",
                color: theme.textPrimary,
              }}
              title={cat.descricao}
            >
              {cat.descricao}
            </Text>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
              }}
            >
              {!isOverlay && (
                <Tag color="geekblue" style={{ margin: 0, fontSize: 10 }}>
                  {dicNatureza[cat.natureza] || "N/A"}
                </Tag>
              )}
              {!isAvailable && !isOverlay && (
                <Button
                  type="text"
                  size="small"
                  icon={<DeleteOutlined />}
                  danger
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation(); // Previne drag start
                    handleUnlink(normalizedCatCode);
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </SortableCard>
    );
  };

  // Helper para agrupar categorias atribuídas
  const groupAssignedCategories = (catCodes) => {
    const assigned = catCodes
      .map((code) => findCategoryByCode(code))
      .filter(Boolean); // Se não achar a categoria, ignora
    return groupLeavesByAncestors(assigned, hierarchySource, { multiLevel });
  };

  // Grupo da coluna de disponíveis: header arrastável + folhas diretas + subgrupos (multinível).
  const renderAvailableGroup = (group) => (
    <div key={group.key}>
      <DraggableGroupHeader groupKey={group.key}>
        <div
          style={{
            padding: "8px 12px",
            margin: "4px 0 8px",
            borderRadius: 4,
            backgroundColor: theme.backgroundTertiary,
            border: `1px solid ${theme.borderSecondary}`,
            display: "flex",
            alignItems: "center",
            gap: 8,
            cursor: "grab",
            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
          }}
          title="Arraste para mover o grupo inteiro"
        >
          <HolderOutlined
            style={{ fontSize: 16, color: theme.textSecondary }}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 0,
            }}
          >
            <Text
              style={{
                fontSize: 12,
                fontWeight: 700,
                textTransform: "uppercase",
                color: theme.textPrimary,
                lineHeight: 1.2,
              }}
            >
              {group.label}
            </Text>
            <Text
              style={{
                fontSize: 10,
                color: theme.textSecondary,
                lineHeight: 1.2,
              }}
            >
              {group.allCategories.length} categorias
            </Text>
          </div>
        </div>
      </DraggableGroupHeader>
      <div
        style={{
          paddingLeft: 12,
          borderLeft: `2px solid ${theme.borderSecondary}`,
          marginLeft: 12,
          marginBottom: 16,
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        {group.categories.map((c) =>
          renderCategoryCard(c.codCategoria, false, true),
        )}
        {group.subgroups.map(renderAvailableGroup)}
      </div>
    </div>
  );

  // Grupo das categorias vinculadas: header só visual, recuado por nível.
  const renderAssignedGroup = (group) => (
    <div
      key={group.key}
      style={group.depth > 0 ? { marginLeft: 12 } : undefined}
    >
      <div
        style={{
          padding: "4px 8px",
          margin: "8px 0 4px",
          borderRadius: 4,
          backgroundColor: theme.backgroundTertiary,
          borderBottom: `1px solid ${theme.borderSecondary}`,
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <Text
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            color: theme.textSecondary,
          }}
        >
          {group.label}
        </Text>
      </div>
      {group.categories.map((c) => renderCategoryCard(c.codCategoria))}
      {group.subgroups.map(renderAssignedGroup)}
    </div>
  );

  // Grupo do modal em massa: "Selecionar grupo" marca todas as folhas descendentes.
  const renderBulkGroup = (group) => (
    <div key={group.key}>
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          padding: "8px 12px",
          paddingLeft: 12 + group.depth * 16,
          backgroundColor: theme.backgroundTertiary,
          borderBottom: `1px solid ${theme.borderSecondary}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <Text
          style={{
            fontSize: 11,
            fontWeight: 700,
            textTransform: "uppercase",
            color: theme.textSecondary,
          }}
        >
          {group.label}
        </Text>
        <Button
          size="small"
          type="text"
          onClick={() => handleBulkToggleGroup(group.allCategories)}
        >
          {group.allCategories.every((c) =>
            bulkSelectedCatCodes.includes(c.codCategoria),
          )
            ? "Desmarcar grupo"
            : "Selecionar grupo"}
        </Button>
      </div>
      {group.categories.length > 0 && (
        <List
          dataSource={group.categories}
          rowKey="codCategoria"
          renderItem={(c) => {
            const isSelected = bulkSelectedCatCodes.includes(c.codCategoria);
            return (
              <List.Item
                key={c.codCategoria}
                style={{
                  padding: "8px 12px",
                  paddingLeft: 12 + group.depth * 16,
                  cursor: "pointer",
                  borderBottom: `1px solid ${theme.borderSecondary}`,
                  backgroundColor: isSelected
                    ? theme.infoBackground
                    : "transparent",
                  transition: "background-color 0.2s",
                }}
                onClick={() => handleBulkToggle(c.codCategoria)}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    gap: 12,
                  }}
                >
                  <Checkbox checked={isSelected} />
                  <div style={{ flex: 1 }}>
                    <Text
                      style={{
                        fontSize: 13,
                        color: theme.textPrimary,
                      }}
                    >
                      {c.descricao}
                    </Text>
                  </div>
                  <Tag color="geekblue" style={{ fontSize: 10 }}>
                    {dicNatureza[c.natureza] || "N/A"}
                  </Tag>
                </div>
              </List.Item>
            );
          }}
        />
      )}
      {group.subgroups.map(renderBulkGroup)}
    </div>
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={(args) => {
        const pointer = pointerWithin(args);
        return pointer.length > 0 ? pointer : rectIntersection(args);
      }}
      onDragStart={({ active }) => setActiveId(active.id)}
      onDragEnd={handleDragEnd}
    >
      <div style={{ display: "flex", height: "calc(100vh - 220px)", gap: 16 }}>
        {/* Coluna Esquerda: Disponíveis */}
        <Card
          size="small"
          title={
            <Space direction="vertical" size={4} style={{ width: "100%" }}>
              <Space>
                <Text strong>CATEGORIAS DISPONIVEIS</Text>
                <Badge
                  count={availableSelectableCategories.length}
                  style={{ backgroundColor: "#108ee9" }}
                />
              </Space>
              {showRevenueToggle && (
                <Space size={8}>
                  <Switch
                    size="small"
                    checked={allowRevenue}
                    onChange={(val) =>
                      typeof onAllowRevenueChange === "function" &&
                      onAllowRevenueChange(val)
                    }
                  />
                  <Text
                    style={{
                      fontSize: 11,
                      color: theme.textSecondary,
                    }}
                  >
                    Incluir categorias de receita
                  </Text>
                </Space>
              )}
            </Space>
          }
          style={{
            flex: "0 0 320px",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
          }}
          styles={{
            body: {
              flex: 1,
              overflowY: "auto",
              padding: 8,
              backgroundColor: theme.backgroundSecondary,
            },
          }}
        >
          <DroppableList
            id="available-container"
            style={{
              minHeight: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <SortableContext
              items={availableSelectableCategories.map((c) => `cat:${c.codCategoria}`)}
              strategy={verticalListSortingStrategy}
            >
              {availableCategoriesGrouped.map(renderAvailableGroup)}
            </SortableContext>
          </DroppableList>
        </Card>

        {/* Coluna Direita: Tipos (Drop Zones) */}
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 16,
            paddingRight: 4,
          }}
        >
          {typeList.length === 0 && (
            <Empty description="Nenhum tipo configurado" />
          )}

          {typeList.map((type) => {
            const assignedCats = configMap[type.value] || [];

            // Filtrar categorias atribuídas baseadas no termo de busca
            const filteredAssignedCats = assignedCats.filter((code) => {
              if (!searchTerm) return true;
              const cat = findCategoryByCode(code);
              if (!cat) return false;
              const lowerSearch = searchTerm.toLowerCase();
              return (
                cat.descricao.toLowerCase().includes(lowerSearch) ||
                String(cat.codCategoria).includes(lowerSearch)
              );
            });

            // Agrupar categorias atribuídas
            const assignedGroups = groupAssignedCategories(filteredAssignedCats);

            return (
              <Card
                key={type.value}
                size="small"
                title={
                  <Space>
                    <Tag color={type.color || "blue"}>{type.value}</Tag>
                    <Text strong style={{ color: theme.textPrimary }}>
                      {type.label}
                    </Text>
                    <Badge
                      count={assignedCats.length}
                      showZero
                      color="#52c41a"
                    />
                  </Space>
                }
                extra={
                  <Button
                    type="text"
                    size="small"
                    icon={<PlusOutlined />}
                    onClick={() => handleBulkOpen(type.value)}
                  >
                    Adicionar
                  </Button>
                }
                styles={{
                  body: { padding: 12, backgroundColor: theme.cardBackground },
                }}
                style={{
                  border: `1px solid ${theme.borderSecondary}`,
                  backgroundColor: theme.cardBackground,
                }}
                headStyle={{
                  borderBottom: `1px solid ${theme.borderSecondary}`,
                  color: theme.textPrimary,
                }}
              >
                <DroppableList
                  id={`type-container:${type.value}`}
                  style={{
                    minHeight: 60,
                    backgroundColor: theme.backgroundSecondary,
                    border: `1px dashed ${theme.borderSecondary}`,
                    borderRadius: 4,
                    padding: 8,
                  }}
                >
                  <SortableContext
                    items={filteredAssignedCats.map((code) => `cat:${code}`)}
                    strategy={verticalListSortingStrategy}
                  >
                    {filteredAssignedCats.length === 0 && (
                      <div
                        style={{
                          textAlign: "center",
                          padding: "16px",
                          color: theme.textSecondary,
                        }}
                      >
                        <Text
                          type="secondary"
                          style={{ fontSize: 12, color: theme.textSecondary }}
                        >
                          {searchTerm
                            ? "Nenhuma categoria encontrada"
                            : "Arraste categorias aqui"}
                        </Text>
                      </div>
                    )}

                    {assignedGroups.map(renderAssignedGroup)}
                  </SortableContext>
                </DroppableList>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Bulk Add Modal */}
      <Modal
        title={`Selecionar Categorias - ${
          typeList.find((t) => t.value === bulkTargetType)?.label || ""
        }`}
        open={isBulkModalOpen}
        onOk={handleBulkSave}
        onCancel={() => setIsBulkModalOpen(false)}
        width={700}
        okText={`Adicionar ${bulkSelectedCatCodes.length} Categorias`}
        cancelText="Cancelar"
        styles={{ body: { maxHeight: "70vh", overflowY: "hidden" } }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "60vh",
            gap: 16,
          }}
        >
          {/* Search and Buttons */}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <Input
              placeholder="Buscar categorias disponíveis..."
              prefix={<SearchOutlined style={{ color: theme.textSecondary }} />}
              value={modalSearch}
              onChange={(e) => setModalSearch(e.target.value)}
              allowClear
              style={{
                flex: 1,
                backgroundColor: theme.inputBackground,
                color: theme.textPrimary,
                borderColor: theme.border,
              }}
            />
            <Space>
              <Button size="small" onClick={handleBulkSelectAll}>
                Marcar Todos
              </Button>
              <Button size="small" onClick={handleBulkDeselectAll}>
                Desmarcar Todos
              </Button>
            </Space>
          </div>

          {/* List */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              border: `1px solid ${theme.borderSecondary}`,
              borderRadius: 8,
              backgroundColor: theme.backgroundSecondary,
            }}
          >
            {filteredCatsForModal.length === 0 ? (
              <div
                style={{
                  padding: 32,
                  textAlign: "center",
                  color: theme.textSecondary,
                }}
              >
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    modalSearch
                      ? "Nenhuma categoria encontrada para o filtro"
                      : "Nenhuma categoria disponível para adicionar"
                  }
                />
              </div>
            ) : (
              <div>
                {filteredCatsForModalGrouped.map(renderBulkGroup)}
              </div>
            )}
          </div>
          <div style={{ textAlign: "right" }}>
            <Text type="secondary" style={{ color: theme.textSecondary }}>
              {bulkSelectedCatCodes.length} selecionadas
            </Text>
          </div>
        </div>
      </Modal>

      <DragOverlay>
        {activeId
          ? (() => {
              const activeIdString = String(activeId);

              // ----------------- NOVO: Preview de Grupo -----------------
              if (activeIdString.startsWith("group:")) {
                const groupKey = activeIdString.replace("group:", "");
                const group = findAvailableGroup(groupKey);
                if (!group) return null;

                return (
                  <div
                    style={{
                      padding: "8px 12px",
                      borderRadius: 4,
                      backgroundColor: theme.backgroundTertiary,
                      border: `1px solid ${theme.primary}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      width: 300,
                      boxShadow: "0 5px 15px rgba(0,0,0,0.15)",
                      cursor: "grabbing",
                    }}
                  >
                    <HolderOutlined
                      style={{ fontSize: 14, color: theme.textSecondary }}
                    />
                    <div style={{ flex: 1 }}>
                      <Text
                        strong
                        style={{
                          display: "block",
                          fontSize: 12,
                          color: theme.textPrimary,
                        }}
                      >
                        {group.label}
                      </Text>
                      <Text
                        type="secondary"
                        style={{ fontSize: 11, color: theme.textSecondary }}
                      >
                        {group.allCategories.length} categorias
                      </Text>
                    </div>
                  </div>
                );
              }

              // ----------------- EXISTENTE: Preview de Categoria -----------------
              const catCode = normalizeCategoryCode(activeIdString.replace("cat:", ""));
              const cat = findCategoryByCode(catCode);
              if (!cat) return null;

              // Check if it is from available list (not in configMap)
              const isAssigned = Object.values(configMap).some((cats) =>
                cats.includes(catCode),
              );
              const isAvailable = !isAssigned;

              return (
                <SortableCard
                  id={activeId}
                  className="cat-chip"
                  bodyStyle={{ padding: "8px 12px" }}
                  style={{
                    backgroundColor: theme.cardBackground,
                    border: `1px solid ${theme.primary}`,
                    borderRadius: 4,
                    width: 280,
                    cursor: "grabbing",
                    boxShadow: "0 5px 15px rgba(0,0,0,0.15)",
                  }}
                >
                  <div
                    style={{ display: "flex", alignItems: "center", gap: 8 }}
                  >
                    <HolderOutlined
                      style={{ fontSize: 14, color: theme.textSecondary }}
                    />
                    <div
                      style={{
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Text
                        strong
                        style={{
                          fontSize: 12,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "block",
                          color: theme.textPrimary,
                        }}
                      >
                        {cat.descricao}
                      </Text>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Tag color="geekblue" style={{ margin: 0, fontSize: 10 }}>
                          {dicNatureza[cat.natureza] || "N/A"}
                        </Tag>
                      </div>
                    </div>
                  </div>
                </SortableCard>
              );
            })()
          : null}
      </DragOverlay>
    </DndContext>
  );
};

export default DragDropConfigurator;
